/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * PayPal approval return page. Popup approvals are handed back to the opener. If the popup was
 * blocked and PayPal used the full page, this route confirms the approval itself before returning
 * to checkout so PayPal contact and address data are already available to the checkout loader.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { routes } from '@/route-paths';
import { confirmPaypalApproval } from '../lib/paypal-client';
import { PAYPAL_APPROVED_MESSAGE, clearPaypalCorrelation, readPaypalCorrelation } from '../lib/paypal-session';
import { createLogger } from '@/lib/logger';
import { getOrCreateCheckoutCorrelationId } from '@/lib/checkout/correlation';

const logger = createLogger();

export default function CybersourcePaypalApproved() {
    const navigate = useNavigate();
    const [message, setMessage] = useState('Completing PayPal…');

    useEffect(() => {
        const correlationId = getOrCreateCheckoutCorrelationId();
        const params = new URLSearchParams(window.location.search);
        const payerId = params.get('PayerID') || params.get('payerID') || params.get('payerId') || '';
        const token = params.get('token') || '';

        if (!payerId || !token) {
            logger.info('PayPal approval return missing required state', { correlationId });
            setMessage('PayPal did not return an approval. You can close this window and try again.');
            return;
        }

        if (window.opener && !window.opener.closed) {
            logger.debug('PayPal approval return handed to opener', { correlationId });
            window.opener.postMessage({ type: PAYPAL_APPROVED_MESSAGE, payerId, token }, window.location.origin);
            setMessage('Approved — completing checkout…');
            return;
        }

        const correlation = readPaypalCorrelation();
        if (!correlation) {
            logger.info('PayPal approval return correlation expired', { correlationId });
            setMessage('Your PayPal session expired. Return to checkout and try again.');
            return;
        }

        const controller = new AbortController();
        void confirmPaypalApproval({ payerId, token, correlation, signal: controller.signal })
            .then(() => {
                clearPaypalCorrelation();
                logger.info('PayPal full-page approval confirmed', { correlationId });
                setMessage('Approved — returning to checkout…');
                void navigate({ pathname: routes.checkout, search: '?payment=paypal' }, { replace: true });
            })
            .catch((error: unknown) => {
                if ((error as { name?: string })?.name === 'AbortError') return;
                logger.info('PayPal full-page approval not confirmed', { correlationId, error });
                setMessage('We could not confirm your PayPal approval. Return to checkout and try again.');
            });

        return () => controller.abort();
    }, [navigate]);

    return (
        <div className="flex min-h-[40vh] items-center justify-center p-8">
            <p className="text-sm text-muted-foreground" role="status">
                {message}
            </p>
        </div>
    );
}
